
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const TaxCalculator: React.FC = () => {
  const [income, setIncome] = useState<number>(75000);
  const [filingStatus, setFilingStatus] = useState<string>("single");
  const [state, setState] = useState<string>("none");
  const [federalTax, setFederalTax] = useState<number>(0);
  const [ficaTax, setFicaTax] = useState<number>(0);
  const [stateTax, setStateTax] = useState<number>(0);
  const [totalTax, setTotalTax] = useState<number>(0);
  const [effectiveTaxRate, setEffectiveTaxRate] = useState<number>(0);
  const [takeHomeIncome, setTakeHomeIncome] = useState<number>(0);

  // 2023 Federal Tax Brackets
  const federalBrackets = {
    single: [
      { rate: 0.10, upTo: 11000 },
      { rate: 0.12, upTo: 44725 },
      { rate: 0.22, upTo: 95375 },
      { rate: 0.24, upTo: 182100 },
      { rate: 0.32, upTo: 231250 },
      { rate: 0.35, upTo: 578125 },
      { rate: 0.37, upTo: Infinity }
    ],
    married: [
      { rate: 0.10, upTo: 22000 },
      { rate: 0.12, upTo: 89450 },
      { rate: 0.22, upTo: 190750 },
      { rate: 0.24, upTo: 364200 },
      { rate: 0.32, upTo: 462500 },
      { rate: 0.35, upTo: 693750 },
      { rate: 0.37, upTo: Infinity }
    ],
    head: [
      { rate: 0.10, upTo: 15700 },
      { rate: 0.12, upTo: 59850 },
      { rate: 0.22, upTo: 95350 },
      { rate: 0.24, upTo: 182100 },
      { rate: 0.32, upTo: 231250 },
      { rate: 0.35, upTo: 578100 },
      { rate: 0.37, upTo: Infinity }
    ]
  };

  // Simplified state tax rates (average) for demonstration
  const stateTaxRates = {
    none: 0,
    california: 0.093,
    newyork: 0.065,
    texas: 0,
    florida: 0,
    illinois: 0.0495,
    pennsylvania: 0.0307,
    ohio: 0.0399,
    georgia: 0.0575,
    northcarolina: 0.0499,
    michigan: 0.0425,
  };

  const calculateTaxes = () => {
    try {
      // Validation
      if (income < 0) {
        toast({
          title: "Invalid input",
          description: "Please enter a valid positive income",
          variant: "destructive"
        });
        return;
      }

      // Calculate Federal Income Tax
      let fedTax = 0;
      let remainingIncome = income;
      let prevBracketLimit = 0;

      const brackets = federalBrackets[filingStatus as keyof typeof federalBrackets];
      
      for (const bracket of brackets) {
        const taxableInBracket = Math.min(remainingIncome, bracket.upTo - prevBracketLimit);
        if (taxableInBracket > 0) {
          fedTax += taxableInBracket * bracket.rate;
          remainingIncome -= taxableInBracket;
        }
        prevBracketLimit = bracket.upTo;
        if (remainingIncome <= 0) break;
      }

      // Calculate FICA taxes (Social Security and Medicare)
      const socialSecurityTax = Math.min(income, 160200) * 0.062; // 6.2% up to wage base limit
      const medicareTax = income * 0.0145; // 1.45% on all earnings
      
      // Additional Medicare tax for high earners
      let additionalMedicareTax = 0;
      if ((filingStatus === "single" && income > 200000) || 
          (filingStatus === "married" && income > 250000)) {
        additionalMedicareTax = (income - (filingStatus === "single" ? 200000 : 250000)) * 0.009;
      }
      
      const totalFica = socialSecurityTax + medicareTax + additionalMedicareTax;
      
      // Calculate State Income Tax (simplified)
      const stateRate = stateTaxRates[state as keyof typeof stateTaxRates] || 0;
      const stateTaxAmount = income * stateRate;
      
      // Calculate totals
      const totalTaxAmount = fedTax + totalFica + stateTaxAmount;
      const effectiveRate = (totalTaxAmount / income) * 100;
      const takeHome = income - totalTaxAmount;
      
      setFederalTax(fedTax);
      setFicaTax(totalFica);
      setStateTax(stateTaxAmount);
      setTotalTax(totalTaxAmount);
      setEffectiveTaxRate(effectiveRate);
      setTakeHomeIncome(takeHome);

      toast({
        title: "Calculation complete",
        description: "Your tax estimate has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="income">Annual Income</Label>
            <Input
              id="income"
              type="number"
              value={income}
              onChange={(e) => setIncome(Number(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label>Filing Status</Label>
            <RadioGroup 
              value={filingStatus} 
              onValueChange={setFilingStatus}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="filing-single" />
                <Label htmlFor="filing-single">Single</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="married" id="filing-married" />
                <Label htmlFor="filing-married">Married Filing Jointly</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="head" id="filing-head" />
                <Label htmlFor="filing-head">Head of Household</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger id="state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No State Tax</SelectItem>
                <SelectItem value="california">California</SelectItem>
                <SelectItem value="newyork">New York</SelectItem>
                <SelectItem value="texas">Texas</SelectItem>
                <SelectItem value="florida">Florida</SelectItem>
                <SelectItem value="illinois">Illinois</SelectItem>
                <SelectItem value="pennsylvania">Pennsylvania</SelectItem>
                <SelectItem value="ohio">Ohio</SelectItem>
                <SelectItem value="georgia">Georgia</SelectItem>
                <SelectItem value="northcarolina">North Carolina</SelectItem>
                <SelectItem value="michigan">Michigan</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={calculateTaxes} className="mt-4 w-full">Calculate Taxes</Button>
          
          <div className="text-sm text-muted-foreground mt-2">
            <p>
              This calculator provides estimates based on 2023 tax rates. Actual taxes may vary.
              Deductions and credits are not included in this simple calculation.
            </p>
          </div>
        </div>

        <div>
          <Card className="border-2 border-primary/20">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Tax Estimation</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Federal Income Tax</div>
                    <div className="text-base font-medium">{federalTax ? formatCurrency(federalTax) : '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">FICA Taxes (SS & Medicare)</div>
                    <div className="text-base font-medium">{ficaTax ? formatCurrency(ficaTax) : '-'}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">State Income Tax</div>
                    <div className="text-base font-medium">{stateTax ? formatCurrency(stateTax) : '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Total Taxes</div>
                    <div className="text-base font-medium">{totalTax ? formatCurrency(totalTax) : '-'}</div>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="text-sm text-muted-foreground">Effective Tax Rate</div>
                  <div className="text-base font-medium">
                    {effectiveTaxRate ? `${effectiveTaxRate.toFixed(2)}%` : '-'}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="text-sm text-muted-foreground">Take-Home Income</div>
                  <div className="text-xl font-bold text-primary">
                    {takeHomeIncome ? formatCurrency(takeHomeIncome) : '-'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {takeHomeIncome ? `(${formatCurrency(takeHomeIncome / 12)} monthly)` : ''}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TaxCalculator;
